import { richTextToPlainText } from 'shared/utils/richText';
import {
  isRenderablePdfBlock,
  PdfBlock,
  PdfContent,
  pdfRichText,
  pdfTable,
  PdfTableCell,
  pdfText,
} from './pdf/pdfBlocks';
import { PDF_CONTENT_WIDTH } from './pdf/pdfPage';
import { boldFont, regularFont } from './pdf/pdfTextMetrics';

/** A caption/value pair (`textWithLabel`). */
export type PdfField = {
  label: string;
  value: string;
  bold?: boolean;
  richText?: boolean;
};

/** Content without a caption (an attachments block): it gets a row merged across the whole table. */
export type PdfFullRowEntry = {
  fullRow: (contentWidth: number) => PdfContent;
};

export type PdfFieldTableEntry = PdfField | PdfFullRowEntry;

const isFullRowEntry = (entry: PdfFieldTableEntry): entry is PdfFullRowEntry =>
  'fullRow' in entry;

/** `MAX_PAIRED_VALUE_LENGTH`: a value up to this many characters shares its row with a second pair. */
const MAX_PAIRED_VALUE_LENGTH = 26;
const CAPTION_FONT = regularFont(8);
const VALUE_MARGIN = { bottom: 8 };
const FULL_ROW_PADDING = 8;

/** `fieldTableColumnRatios`: caption/value pairs spread evenly across the row. */
export function fieldTableColumnRatios(pairsPerRow: number, captionShare = 0.26): number[] {
  const pairWidth = 100 / pairsPerRow;
  return Array.from({ length: pairsPerRow }, () => [
    pairWidth * captionShare,
    pairWidth * (1 - captionShare),
  ]).flat();
}

function fitsPairedCell({ value, richText }: PdfField): boolean {
  const plain = richText ? richTextToPlainText(value) : value;
  const longestLine = Math.max(
    0,
    ...plain.split('\n').map((line) => line.replace(/\s+/g, ' ').trim().length)
  );
  return longestLine <= MAX_PAIRED_VALUE_LENGTH;
}

const captionCell = ({ label }: PdfField): PdfTableCell => ({
  content: (width) => [pdfText(label, width, { font: CAPTION_FONT })],
});

function valueContent(field: PdfField, width: number): PdfBlock {
  const font = field.bold ? boldFont() : regularFont();
  return field.richText
    ? pdfRichText(field.value, width, { font, margin: VALUE_MARGIN })
    : pdfText(field.value, width, { font, margin: VALUE_MARGIN });
}

/**
 * `inspectionFieldTable.fieldTableRows`: short values pair up, a long one takes the full width, a half-filled
 * row is closed by widening its last cell, and an empty full-row block is dropped.
 */
export function fieldTableRows(
  entries: PdfFieldTableEntry[],
  pairsPerRow: number,
  tableWidth = PDF_CONTENT_WIDTH
): PdfTableCell[][] {
  const columnCount = pairsPerRow * 2;
  const rows: PdfTableCell[][] = [];
  let pending: PdfTableCell[] = [];

  const flushPending = (): void => {
    if (pending.length === 0) {
      return;
    }
    const missing = columnCount - pending.length;
    const lastCell = pending[pending.length - 1];
    pending[pending.length - 1] = { ...lastCell, colSpan: (lastCell.colSpan ?? 1) + missing };
    rows.push(pending);
    pending = [];
  };

  entries.forEach((entry) => {
    if (isFullRowEntry(entry)) {
      const block = entry.fullRow(tableWidth - FULL_ROW_PADDING);
      if (!isRenderablePdfBlock(block)) {
        return;
      }
      flushPending();
      rows.push([{ colSpan: columnCount, content: () => [block] }]);
      return;
    }
    const valueCell: PdfTableCell = { content: (width) => [valueContent(entry, width)] };
    if (columnCount === 2 || !fitsPairedCell(entry)) {
      flushPending();
      rows.push([captionCell(entry), { ...valueCell, colSpan: columnCount - 1 }]);
      return;
    }
    pending.push(captionCell(entry), valueCell);
    if (pending.length === columnCount) {
      flushPending();
    }
  });
  flushPending();
  return rows;
}

export type PdfFieldTableSpec = {
  entries: PdfFieldTableEntry[];
  pairsPerRow: number;
  captionShare?: number;
  /** Rows drawn above the fields, e.g. the register entry header. */
  leadingRows?: PdfTableCell[][];
  margin?: { top?: number };
};

export function pdfFieldTable({
  entries,
  pairsPerRow,
  captionShare,
  leadingRows = [],
  margin,
}: PdfFieldTableSpec): PdfBlock {
  return pdfTable(
    {
      columnRatios: fieldTableColumnRatios(pairsPerRow, captionShare),
      rows: [...leadingRows, ...fieldTableRows(entries, pairsPerRow)],
      margin,
    },
    PDF_CONTENT_WIDTH
  );
}
